import React, {useEffect, useState} from 'react'
import {Button, Dropdown, Loader} from 'rsuite'
import {useExportEmployeesSafeLazyQuery} from '../../../api'
import {useTranslation} from 'react-i18next'

interface employeeCsvInfo {
  content: {
    name: string
    _function: {de: string; fr: string; en: string}
    jobTitleRef?: {de?: string; fr?: string; en?: string}
    divisionList_DEPRECATED?: any[]
    organizationUnitList?: any[]
    phoneNumber: string
    eMail: string
    image?: {
      media?: {
        id?: string
        url?: string
      } | null
    } | null
    hide: boolean
    hideImage: boolean
  }
  publicationState?: string
  publicationDate?: string
  dePublicationDate?: string
  createdAt?: string
}

enum employeeState {
  All,
  Published,
  Depublished,
  Archived,
  Draft,
}

export default function CustomViewExportEmployees() {
  const [exportEmployees, {data, loading, error}] = useExportEmployeesSafeLazyQuery({
    fetchPolicy: 'no-cache',
  })

  const [blob, setBlob] = useState<Blob>()
  const {t} = useTranslation()
  const [filterState, setFilterState] = useState<employeeState>(employeeState.Published)

  const date = new Date()
  const timestamp = date.toLocaleDateString('de-CH').split('.').reverse().join('')
  const settings = document.getElementById('settings')

  useEffect(() => {
    if (blob) {
      let fileName = `employees_${timestamp}.csv`
      if (settings?.textContent) {
        const {environmentName} = JSON.parse(settings.textContent)
        fileName = `employees_${environmentName}_${timestamp}.csv`
      }

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', fileName)

      // Append to html link element page
      document.body.appendChild(link)

      // Start download
      link.click()

      // Clean up and remove the link
      link?.parentNode?.removeChild(link)
    }
  }, [blob, timestamp])

  const downloadCSV = async () => {
    const result = await exportEmployees()
    const data = result.data

    if (data && data.extensions && data.extensions.employeeExportSafe) {
      if (!data.extensions.employeeExportSafe.nodes) {
        console.error('No nodes found in the response')
        alert('No employee data found in the response.')
        return
      }

      const csvHeader =
        'name; organizationUnitDe; organizationUnitFr; organizationUnitEn; division_De; division_Fr; division_En; functionDe; functionFr; functionEn; jobTitleDe; jobTitleFr; jobTitleEn; phoneNumber; eMail; imageUrl; imageId; hideEmployee; hideImage; status; createdAt'

      const processedData = processEmployeeData(
        data.extensions.employeeExportSafe.nodes,
        filterState
      )

      if (processedData.length === 0) {
        console.warn('No employees match the current filter. Cannot generate CSV.')
        // You could show a user-friendly message here instead of generating an empty CSV
        alert('No employees match the current filter criteria.')
        return
      }

      const csvBody = processedData.map((e: string[]) => e.join(';')).join('\n')
      const csvData = csvHeader + '\n' + csvBody
      const blob = new Blob([csvData], {type: 'octet/stream'})
      setBlob(() => blob)
    } else {
      console.error('Invalid data structure received from API')
      alert('Invalid data received from the server.')
    }
  }

  const getTitle = () => {
    switch (filterState) {
      case employeeState.All:
        return `${t('exportEmployees.state')}: ${t('exportEmployees.all')}`
      case employeeState.Published:
        return `${t('exportEmployees.state')}: ${t('exportEmployees.published')}`
      case employeeState.Depublished:
        return `${t('exportEmployees.state')}: ${t('exportEmployees.depublished')}`
      // case employeeState.Archived:
      //   return `${t('exportEmployees.state')}: ${t('exportEmployees.archived')}`
      // case employeeState.Draft:
      //   return `${t('exportEmployees.state')}: ${t('exportEmployees.draft')}`
      default:
        return `${t('exportEmployees.state')}: ${t('exportEmployees.all')}`
    }
  }

  return (
    <>
      <h3>{t('exportEmployees.title')}</h3>
      <br />
      <br />
      <div>
        {error && <p>{t('exportEmployees.error')}</p>}
        {loading && <p>{t('exportEmployees.loading')}</p>}
      </div>
      <br />
      <div>
        <Dropdown title={getTitle()}>
          <Dropdown.Item onClick={() => setFilterState(employeeState.All)}>
            {t('exportEmployees.all')}
          </Dropdown.Item>
          <Dropdown.Item onClick={() => setFilterState(employeeState.Published)}>
            {t('exportEmployees.published')}
          </Dropdown.Item>
          <Dropdown.Item onClick={() => setFilterState(employeeState.Depublished)}>
            {t('exportEmployees.depublished')}
          </Dropdown.Item>
          {/* <Dropdown.Item onClick={() => setFilterState(employeeState.Draft)}>
            {t('exportEmployees.draft')}
          </Dropdown.Item> */}
          {/* <Dropdown.Item onClick={() => setFilterState(employeeState.Archived)}>
            {t('exportEmployees.archived')}
          </Dropdown.Item> */}
        </Dropdown>

        <br />
        <br />

        <Button
          appearance="primary"
          size="lg"
          onClick={downloadCSV}
          disabled={loading}
          style={{
            marginBottom: '1rem',
          }}>
          {loading && <Loader size="xs" style={{marginRight: '8px'}} />}
          {t('exportEmployees.download')}
        </Button>
      </div>
    </>
  )
}

const getPublicationState = (
  publicationState: string,
  publicationDateString?: string,
  depublicationDateString?: string
) => {
  if (publicationState === 'Published' && publicationDateString) {
    const publicationDate = new Date(publicationDateString)
    const now = new Date()

    if (!publicationDate || now.getTime() >= publicationDate.getTime()) {
      // Check if the article is depublicated
      if (depublicationDateString) {
        const depublicationDate = new Date(depublicationDateString)
        if (!depublicationDate || now.getTime() < depublicationDate.getTime()) {
          return 'Published'
        } else {
          return 'Depublished'
        }
      } else {
        return 'Published'
      }
    } else {
      return 'Pending'
    }
  } else {
    return publicationState
  }
}

const processEmployeeData = (data: any, filterState: employeeState) => {
  // Handle empty or invalid data
  if (!data || !Array.isArray(data) || data.length === 0) {
    console.log('No data to process or data is empty')
    return []
  }

  const employeeArray = data
    .filter((item: employeeCsvInfo) => {
      // Exclude null/undefined items and items with publicationState 'Trash' or 'Draft'
      return (
        item &&
        item.publicationState &&
        item.publicationState !== 'Trash' &&
        item.publicationState !== 'Draft'
      )
    })
    .filter((item: employeeCsvInfo) => {
      if (!item || !item.publicationState) return false

      if (filterState === employeeState.All) {
        return true
      } else if (filterState === employeeState.Published) {
        const depublicationDate = new Date(item?.dePublicationDate || '')
        const now = new Date()

        if (item.publicationState === 'Published') {
          if (!isNaN(depublicationDate.getTime())) {
            if (now.getTime() >= depublicationDate.getTime()) {
              return false
            }
          }

          return true
        }
        return false
      } else if (filterState === employeeState.Archived) {
        return item.publicationState === 'Archived'
      } else if (filterState === employeeState.Depublished) {
        const depublicationDate = new Date(item?.dePublicationDate || '')
        const now = new Date()
        if (depublicationDate && now.getTime() >= depublicationDate.getTime()) {
          return true
        }
        return false
      } else if (filterState === employeeState.Draft) {
        return item.publicationState === 'Draft'
      }

      return true
    })
    .map((item: employeeCsvInfo) => {
      if (!item) return []

      const stdValues = {
        name: item.content.name,
        organizationUnitDe: '',
        organizationUnitFr: '',
        organizationUnitEn: '',
        divisionDe: '',
        divisionFr: '',
        divisionEn: '',
        functionDe: item.content._function.de,
        functionFr: item.content._function.fr,
        functionEn: item.content._function.en,
        jobTitleDe: item.content.jobTitleRef?.de,
        jobTitleFr: item.content.jobTitleRef?.fr,
        jobTitleEn: item.content.jobTitleRef?.en,
        phoneNumber: item.content.phoneNumber,
        eMail: item.content.eMail,
        imageUrl: item.content?.image?.media?.url || '',
        imageId: item.content?.image?.media?.id || '',
        hide: item.content.hide,
        hideImage: item.content.hideImage,
        status: getPublicationState(
          item.publicationState || '',
          item.publicationDate,
          item.dePublicationDate
        ),
        createdAt: item.createdAt || '',
      }

      const employeeArray = []

      const orgUnits_length = item.content.organizationUnitList?.length || 0

      if (item.content.organizationUnitList && orgUnits_length) {
        for (let idx = 0; idx < orgUnits_length; idx++) {
          const obj = {
            name: stdValues.name,
            organizationUnitDe:
              item.content.organizationUnitList[idx].organizationUnit.record.content.title.de,
            organizationUnitFr:
              item.content.organizationUnitList[idx].organizationUnit.record.content.title.fr,
            organizationUnitEn:
              item.content.organizationUnitList[idx].organizationUnit.record.content.title.en,
            divisionDe:
              item.content.organizationUnitList[idx].organizationUnit.record.content?.division
                ?.record?.content?.title?.de,
            divisionFr:
              item.content.organizationUnitList[idx].organizationUnit.record.content?.division
                ?.record?.content?.title?.fr,
            divisionEn:
              item.content.organizationUnitList[idx].organizationUnit.record.content?.division
                ?.record?.content?.title?.en,
            functionDe: stdValues.functionDe,
            functionFr: stdValues.functionFr,
            functionEn: stdValues.functionEn,
            jobTitleDe: stdValues.jobTitleDe,
            jobTitleFr: stdValues.jobTitleFr,
            jobTitleEn: stdValues.jobTitleEn,
            phoneNumber: stdValues.phoneNumber,
            eMail: stdValues.eMail,
            imageUrl: stdValues.imageUrl || '',
            imageId: stdValues.imageId || '',
            hide: stdValues.hide,
            hideImage: stdValues.hideImage,
            status: stdValues.status,
            createdAt: stdValues.createdAt,
          }
          const arrValues = Object.values(obj)
          const escapedArr = arrValues.map((val) => JSON.stringify(val))

          employeeArray.push(escapedArr)
        }
      } else {
        const obj = stdValues
        const arrValues = Object.values(obj)
        const escapedArr = arrValues.map((val) => JSON.stringify(val))
        employeeArray.push(escapedArr)
      }

      return employeeArray
    })
    .flat(1)

  // Handle case where no items match the filter criteria
  if (employeeArray.length === 0) {
    console.log('No employees match the current filter criteria')
  }

  return employeeArray
}
